"use client"


import { memo, useState } from "react"
import Link from "next/link"
import { useAuth } from "@/hooks/use-auth"
import { Button } from "@/components/ui/button"
import { Calculator, Book, FileText, Check, Box, BookA, Wand2, Lightbulb, Pencil, Eye, Download, Camera, Highlighter, Upload, PenLine, Share2 } from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import { QuestionResponse } from "@/services/practice"
import { Info, Disc, Square } from "lucide-react"
import { cn, btn3d } from "@/lib/utils"
import { useScreenRecorder } from "@/hooks/use-screen-recorder"
import { createPortal } from "react-dom"
import { useEffect, useRef } from "react"
import { AnnotationOverlay } from "@/components/recording/annotation-overlay"
import { RecordingPreviewModal } from "@/components/recording/recording-preview-modal"
import { adminQuestionsService } from "@/services/admin-questions"
import { MetadataEditorModal } from "@/components/dashboard/practice/session/metadata-editor-modal"

export type AssistTool = 'dictionary' | 'vocab' | 'calculator';

interface SessionHeaderProps {
    title: string;
    question?: QuestionResponse | null;
    elapsedTime: number; // Seconds
    onToggleInfo: () => void;
    isInfoVisible: boolean;
    // New props for drop-down toolbox
    activeTool?: AssistTool | null;
    onToolChange?: (tool: AssistTool | null) => void;
    showSolutionButton?: boolean;
    onShowSolution?: () => void;
    showRecordingButton?: boolean;

    // Review Mode props
    onDownloadPDF?: () => void;
    onDownloadSnapshot?: () => void;

    // Calculator props
    isMath?: boolean;
    isCalculatorOpen?: boolean;
    onToggleCalculator?: () => void;

    // Reference sheet props
    onToggleReference?: () => void;

    // Highlighter toggle (reading & writing only)
    isHighlightActive?: boolean;
    onToggleHighlight?: () => void;

    // Callback for question updates
    onQuestionUpdate?: (updatedQuestion: QuestionResponse) => void;

    // Share the current question as a snapshot image
    onShare?: () => void;
}

// Helper functions
function formatTime(seconds: number) {
    if (isNaN(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

function WebcamPreview({ stream }: { stream: MediaStream }) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const [position, setPosition] = useState({
        x: typeof window !== 'undefined' ? window.innerWidth - 220 : 0,
        y: typeof window !== 'undefined' ? window.innerHeight - 170 : 0
    })
    const [isDragging, setIsDragging] = useState(false)
    const dragOffset = useRef({ x: 0, y: 0 })

    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream
        }
    }, [stream])

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true)
        dragOffset.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        }
    }

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                setPosition({
                    x: e.clientX - dragOffset.current.x,
                    y: e.clientY - dragOffset.current.y
                })
            }
        }
        const handleMouseUp = () => {
            setIsDragging(false)
        }
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove)
            window.addEventListener('mouseup', handleMouseUp)
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isDragging])

    return (
        <div
            className={cn(
                "fixed z-[9999] w-48 h-36 bg-black rounded-lg shadow-lg overflow-hidden border-2 border-primary/20 animate-in fade-in slide-in-from-bottom-10 cursor-move active:cursor-grabbing",
                isDragging && "scale-105 shadow-xl transition-transform"
            )}
            style={{ left: position.x, top: position.y }}
            onMouseDown={handleMouseDown}
        >
            <video
                ref={videoRef}
                autoPlay
                muted
                className="w-full h-full object-cover mirror-mode pointer-events-none"
            />
        </div>
    )
}

export const SessionHeader = memo(function SessionHeader({
    title,
    // ... existing props

    question,
    elapsedTime,
    onToggleInfo,
    isInfoVisible,
    activeTool,
    onToolChange,
    showSolutionButton,
    onShowSolution,
    showRecordingButton = true,
    onDownloadPDF,
    onDownloadSnapshot,
    isMath = false,
    isCalculatorOpen,
    onToggleCalculator,
    onToggleReference,
    isHighlightActive = false,
    onToggleHighlight,
    onQuestionUpdate,
    onShare
}: SessionHeaderProps) {
    const [isTimerVisible, setIsTimerVisible] = useState(true)
    const [isMetadataModalOpen, setIsMetadataModalOpen] = useState(false)
    const { user } = useAuth()

    // Screen Recording Logic
    const { isRecording, startRecording, stopRecording, webcamStream, recordedBlob, clearRecording } = useScreenRecorder()
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [statusModal, setStatusModal] = useState<{ open: boolean; type: 'success' | 'error'; message: string }>({
        open: false, type: 'success', message: ''
    })
    const [isSavingTutorial, setIsSavingTutorial] = useState(false)
    const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false)
    // Drawing/annotation tools — same overlay used during recording, toggleable on demand.
    const [showDrawingTools, setShowDrawingTools] = useState(false)

    // A solution video chosen from disk (instead of recorded) flows through the same preview/save modal.
    const [uploadedBlob, setUploadedBlob] = useState<Blob | null>(null)
    const uploadInputRef = useRef<HTMLInputElement>(null)
    const previewBlob = recordedBlob || uploadedBlob

    // Capture the question at the moment recording STARTS, so that navigating to another
    // question before stopping does NOT change which question the tutorial is saved to.
    const recordingQuestionRef = useRef<typeof question | null>(null)

    const handleStartRecording = (withWebcam: boolean) => {
        recordingQuestionRef.current = question  // snapshot current question
        startRecording(withWebcam)
    }

    const handleUploadSolutionFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = "" // allow re-selecting the same file later
        if (!file) return
        if (!file.type.startsWith("video/")) {
            setStatusModal({ open: true, type: 'error', message: 'Please select a valid video file.' })
            return
        }
        recordingQuestionRef.current = question  // snapshot current question
        setUploadedBlob(file)
        setIsPreviewOpen(true)
    }

    // Handle recording completion
    useEffect(() => {
        if (recordedBlob) {
            // Check if user is Instructor(4), Admin(5), or Developer(6)
            // Or "Higher than instructor or higher" -> >= 4
            const role = user?.role || 1
            if (role >= 4) {
                setIsPreviewOpen(true)
            } else {
                handleDownload(recordedBlob)
                clearRecording()
            }
        }
    }, [recordedBlob, user])

    const handleDownload = (blob: Blob | null) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `recording-${new Date().toISOString()}.webm`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const handleSaveTutorial = async (language: 'bn' | 'en', trimmedBlob?: Blob) => {
        const finalBlob = trimmedBlob || recordedBlob || uploadedBlob
        // Use the question that was active when recording STARTED / file was chosen, not the current one
        const targetQuestion = recordingQuestionRef.current
        if (!finalBlob || !targetQuestion) return

        setIsSavingTutorial(true)
        try {
            // 1. Upload Video
            const { url } = await adminQuestionsService.uploadVideo(finalBlob)

            // 2. Update the question where recording started (not the current question)
            await adminQuestionsService.updateQuestion(targetQuestion.questionId || String(targetQuestion.id), {
                tutorial_link: url
            })

            console.log("Tutorial saved to question:", targetQuestion.questionId || targetQuestion.id, url)
            setStatusModal({
                open: true,
                type: 'success',
                message: `Tutorial saved successfully to question ${targetQuestion.questionId || targetQuestion.id}!`
            })

            setIsPreviewOpen(false)
            clearRecording()
            setUploadedBlob(null)
            recordingQuestionRef.current = null

        } catch (error) {
            console.error("Failed to save tutorial:", error)
            setStatusModal({
                open: true,
                type: 'error',
                message: "Failed to save tutorial. Please try again."
            })
        } finally {
            setIsSavingTutorial(false)
        }
    }

    const handleClosePreview = () => {
        setIsPreviewOpen(false)
        clearRecording()
    }

    return (
        <>
            <MetadataEditorModal
                isOpen={isMetadataModalOpen}
                onClose={() => setIsMetadataModalOpen(false)}
                question={question as any}
                onUpdate={(q) => onQuestionUpdate?.(q as any)}
            />
            {/* Webcam PiP Overlay */}
            {isRecording && webcamStream && typeof document !== 'undefined' && createPortal(
                <WebcamPreview stream={webcamStream} />,
                document.body
            )}

            {/* Annotation Tools Overlay — during recording or when toggled on */}
            {(isRecording || showDrawingTools) && <AnnotationOverlay />}

            {/* Hidden picker for uploading a solution video from disk */}
            <input
                ref={uploadInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleUploadSolutionFile}
            />

            {previewBlob && (
                <RecordingPreviewModal
                    isOpen={isPreviewOpen}
                    onClose={() => { setIsPreviewOpen(false); setUploadedBlob(null) }}
                    blob={previewBlob}
                    onDownload={() => handleDownload(previewBlob)}
                    onSaveTutorial={handleSaveTutorial}
                    isSaving={isSavingTutorial}
                />
            )}

            <AlertDialog open={statusModal.open} onOpenChange={(open) => setStatusModal(prev => ({ ...prev, open }))}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{statusModal.type === 'success' ? 'Success' : 'Error'}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {statusModal.message}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction onClick={() => setStatusModal(prev => ({ ...prev, open: false }))}>
                            OK
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <header
                className="relative h-14 sm:h-16 flex items-center justify-between px-3 sm:px-4 md:px-6 bg-background"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(to right, var(--border) 0 12px, transparent 12px 20px)",
                    backgroundSize: "100% 1.5px",
                    backgroundPosition: "bottom",
                    backgroundRepeat: "no-repeat",
                }}
            >
                <div className="flex items-center gap-2 sm:gap-4">
                    {/* ... (keep left section) */}
                    <div className="flex flex-col min-w-0">
                        <span className="font-bold text-xl md:text-2xl tracking-tight truncate max-w-[200px] sm:max-w-[300px] md:max-w-[400px]" title={title}>{title}</span>
                        <div className="flex items-center gap-2">
                            <button
                                aria-label="Toggle Information"
                                onClick={onToggleInfo}
                                className="text-base text-muted-foreground flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
                            >
                                Information <span className={`text-base transition-transform duration-200 ${!isInfoVisible ? '-rotate-90' : ''}`}>▼</span>
                            </button>

                            {question && (
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <button aria-label="View Question Details" className="text-base text-primary hover:underline flex items-center gap-1">
                                            <Info className="h-3 w-3" />
                                            Details
                                        </button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        {/* ... (keep dialog content) */}
                                        <DialogHeader>
                                            <DialogTitle>Question Details</DialogTitle>
                                            <DialogDescription>
                                                Metadata and reviewer information for this question.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="grid grid-cols-2 gap-4 text-sm mt-4">
                                            {/* ... (keep details grid) */}
                                            <div>
                                                <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Question ID</span>
                                                <span className="font-mono text-sm">{question.questionId}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Internal ID</span>
                                                <span className="font-mono text-sm">{question.id}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Reviewer</span>
                                                <span className="font-medium text-primary">
                                                    {question.reviewerName || (question.reviewerId ? `Assigned (ID: ${question.reviewerId})` : "None")}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Status</span>
                                                <span className="capitalize">{question.userContext?.isMastered ? "Mastered" : "Active"}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Domain</span>
                                                <span>{question.domainName}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Skill</span>
                                                <span>{question.skillName}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Difficulty</span>
                                                <span>{question.difficulty}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Provider</span>
                                                <span>{question.providerCode || question.providerName}</span>
                                            </div>

                                            {/* Tutorial Upload Tracking */}
                                            {question.tutorialLink && question.tutorialUploadedByName && (
                                                <>
                                                    <div className="col-span-2 border-t pt-3 mt-2">
                                                        <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider mb-1">Tutorial Video</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Uploaded By</span>
                                                        <span className="font-medium text-green-600 dark:text-green-400">{question.tutorialUploadedByName}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Uploaded At</span>
                                                        <span className="text-sm">{question.tutorialUploadedAt ? new Date(question.tutorialUploadedAt).toLocaleString() : "N/A"}</span>
                                                    </div>
                                                </>
                                            )}

                                            {/* Voice Note Upload Tracking */}
                                            {question.voiceNoteUrl && question.voiceNoteUploadedByName && (
                                                <>
                                                    <div className="col-span-2 border-t pt-3 mt-2">
                                                        <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider mb-1">Voice Note</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Uploaded By</span>
                                                        <span className="font-medium text-blue-600 dark:text-blue-400">{question.voiceNoteUploadedByName}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Uploaded At</span>
                                                        <span className="text-sm">{question.voiceNoteUploadedAt ? new Date(question.voiceNoteUploadedAt).toLocaleString() : "N/A"}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            )}
                        </div>
                    </div>

                    {/* Record & Review — placed next to the domain title (desktop) */}
                    <div className="hidden md:flex items-center gap-2">
                        {showRecordingButton && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        {isRecording ? (
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={stopRecording}
                                                aria-label="Stop Recording"
                                                className={cn(
                                                    "h-8 text-base gap-2 rounded-full border-primary/20 animate-pulse",
                                                    btn3d
                                                )}
                                            >
                                                <Square className="h-3.5 w-3.5 fill-current" />
                                                <span className="hidden xl:inline">Stop Rec</span>
                                            </Button>
                                        ) : (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        aria-label="Recording Options"
                                                        className={cn(
                                                            "h-8 text-base gap-2 rounded-full border-primary/20 bg-card",
                                                            btn3d
                                                        )}
                                                    >
                                                        <Disc className="h-3.5 w-3.5 text-red-500" />
                                                        <span className="hidden xl:inline">Record</span>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="start">
                                                    <DropdownMenuItem onClick={() => handleStartRecording(true)} className="cursor-pointer">
                                                        <Camera className="mr-2 h-4 w-4" />
                                                        Record with Camera
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handleStartRecording(false)} className="cursor-pointer">
                                                        <Disc className="mr-2 h-4 w-4" />
                                                        Record Screen Only
                                                    </DropdownMenuItem>
                                                    {(user?.role || 1) >= 4 && (
                                                        <>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem onClick={() => uploadInputRef.current?.click()} className="cursor-pointer">
                                                                <Upload className="mr-2 h-4 w-4" />
                                                                Upload Solution Video
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{isRecording ? "Stop Recording" : "Recording Options"}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}

                        {(user?.role || 1) >= 3 && (
                            <>
                                <DropdownMenu>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className={cn(
                                                            "h-8 text-base gap-2 rounded-full border-primary/20 bg-card text-muted-foreground hover:text-foreground",
                                                            btn3d
                                                        )}
                                                    >
                                                        <Eye className="h-3.5 w-3.5" />
                                                        <span className="hidden xl:inline">Review</span>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Review Options</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    <DropdownMenuContent align="start" className="w-56">
                                        <DropdownMenuLabel>Review Actions</DropdownMenuLabel>
                                        <DropdownMenuSeparator />

                                        <DropdownMenuItem onSelect={() => setIsInfoDialogOpen(true)}>
                                            <Info className="mr-2 h-4 w-4" />
                                            <span>Question Info</span>
                                        </DropdownMenuItem>

                                        {onShowSolution && (
                                            <DropdownMenuItem onSelect={onShowSolution}>
                                                <Lightbulb className="mr-2 h-4 w-4" />
                                                <span>View Solution</span>
                                            </DropdownMenuItem>
                                        )}

                                        {question && user && (user.role || 1) >= 3 && (
                                            <DropdownMenuItem onSelect={() => setIsMetadataModalOpen(true)}>
                                                <Wand2 className="mr-2 h-4 w-4" />
                                                <span>Edit Metadata</span>
                                            </DropdownMenuItem>
                                        )}

                                        {onDownloadPDF && (
                                            <DropdownMenuItem onSelect={onDownloadPDF}>
                                                <Download className="mr-2 h-4 w-4" />
                                                <span>Download PDF</span>
                                            </DropdownMenuItem>
                                        )}

                                        {onDownloadSnapshot && (
                                            <DropdownMenuItem onSelect={onDownloadSnapshot}>
                                                <Camera className="mr-2 h-4 w-4" />
                                                <span>Snapshot</span>
                                            </DropdownMenuItem>
                                        )}

                                        {question && (
                                            <DropdownMenuItem asChild>
                                                <Link href={`/admin/questions/edit/${question.questionId}`} target="_blank" className="flex items-center w-full cursor-pointer">
                                                    <Pencil className="mr-2 h-4 w-4" />
                                                    <span>Edit Question</span>
                                                </Link>
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                {/* Question Info Dialog - Controlled by State */}
                                <Dialog open={isInfoDialogOpen} onOpenChange={setIsInfoDialogOpen}>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Question Details</DialogTitle>
                                            <DialogDescription>
                                                Metadata and reviewer information for this question.
                                            </DialogDescription>
                                        </DialogHeader>
                                        {question && (
                                            <div className="grid grid-cols-2 gap-4 text-sm mt-4">
                                                <div>
                                                    <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Question ID</span>
                                                    <span className="font-mono text-sm">{question.questionId}</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Internal ID</span>
                                                    <span className="font-mono text-sm">{question.id}</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Reviewer</span>
                                                    <span className="font-medium text-primary">
                                                        {question.reviewerName || (question.reviewerId ? `Assigned (ID: ${question.reviewerId})` : "None")}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Status</span>
                                                    <span className="capitalize">{question.userContext?.isMastered ? "Mastered" : "Active"}</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Domain</span>
                                                    <span>{question.domainName}</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Skill</span>
                                                    <span>{question.skillName}</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Difficulty</span>
                                                    <span>{question.difficulty}</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-muted-foreground block text-sm uppercase tracking-wider">Provider</span>
                                                    <span>{question.providerCode || question.providerName}</span>
                                                </div>
                                            </div>
                                        )}
                                    </DialogContent>
                                </Dialog>
                            </>
                        )}

                        {(user?.role || 1) >= 3 && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant={showDrawingTools ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setShowDrawingTools((v) => !v)}
                                            aria-label="Toggle Drawing Tools"
                                            className={cn(
                                                "h-8 text-base gap-2 rounded-full border-primary/20",
                                                btn3d,
                                                showDrawingTools
                                                    ? "bg-primary/90 text-primary-foreground hover:bg-primary/90"
                                                    : "bg-card text-muted-foreground hover:text-foreground"
                                            )}
                                        >
                                            <PenLine className="h-3.5 w-3.5" />
                                            <span className="hidden xl:inline">Draw</span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Drawing Tools</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}

                        {/* Edit Button - in left group with Record / Review / Draw */}
                        {user && (user.role || 1) >= 3 && question && (
                            <Link href={`/admin/questions/edit/${question.questionId}`} target="_blank">
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                aria-label="Edit Question"
                                                className={cn("h-8 text-base gap-2 rounded-full border-primary/20 bg-card text-muted-foreground hover:text-foreground hover:bg-muted animate-in fade-in", btn3d)}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                                <span className="hidden xl:inline">Edit</span>
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Edit Question</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </Link>
                        )}
                    </div>
                </div>

                {/* Timer - Hidden on desktop (centered), shown on mobile (right side) */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex flex-col items-center">
                    <span className="font-bold text-2xl md:text-3xl tabular-nums">
                        {isTimerVisible ? formatTime(elapsedTime) : "**:**"}
                    </span>
                    <button
                        onClick={() => setIsTimerVisible(!isTimerVisible)}
                        aria-label={isTimerVisible ? "Hide Timer" : "Show Timer"}
                        className="text-base bg-muted px-2 py-0.5 rounded-full text-muted-foreground hover:bg-muted/80"
                    >
                        {isTimerVisible ? "HIDE" : "SHOW"}
                    </button>
                </div>

                {/* Right side controls */}
                <div className="flex items-center gap-2 md:gap-4">
                    {/* Timer for mobile - shown on right */}
                    <div className="flex md:hidden flex-col items-end mr-2">
                        <span className="font-bold text-lg tabular-nums">
                            {isTimerVisible ? formatTime(elapsedTime) : "**:**"}
                        </span>
                        <button
                            onClick={() => setIsTimerVisible(!isTimerVisible)}
                            aria-label={isTimerVisible ? "Hide Timer" : "Show Timer"}
                            className="text-base bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground hover:bg-muted/80"
                        >
                            {isTimerVisible ? "HIDE" : "SHOW"}
                        </button>
                    </div>

                    {/* Mode Toggles: Assist Mode and Review Mode */}
                    <div className="hidden md:flex items-center gap-2">
                        {/* Share Button - Always visible: exports a snapshot of the question */}
                        {onShare && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            aria-label="Share Question"
                                            onClick={onShare}
                                            className={cn(
                                                "h-8 text-base gap-2 rounded-full border-primary/20 bg-card text-muted-foreground hover:text-foreground",
                                                btn3d
                                            )}
                                        >
                                            <Share2 className="h-3.5 w-3.5" />
                                            <span className="hidden xl:inline">Share</span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Share as image</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}

                        {/* Math Calculator Button - Always visible for Math */}
                        {isMath && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant={isCalculatorOpen ? "default" : "outline"}
                                            size="sm"
                                            aria-label="Toggle Calculator"
                                            onClick={onToggleCalculator}
                                            className={cn(
                                                "h-8 text-base gap-2 rounded-full border-primary/20",
                                                btn3d,
                                                isCalculatorOpen
                                                    ? "bg-primary/90 text-primary-foreground hover:bg-primary/90"
                                                    : "bg-card text-muted-foreground hover:text-foreground"
                                            )}
                                        >
                                            <Calculator className="h-3.5 w-3.5" />
                                            <span className="hidden xl:inline">Calculator</span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Calculator</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}

                        {/* Solution Button - Visible when allowed (e.g. after check or review mode) */}
                        {showSolutionButton && onShowSolution && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={onShowSolution}
                                            className={cn("h-8 text-base gap-2 rounded-full border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-300 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20 animate-in fade-in zoom-in", btn3d)}
                                        >
                                            <Lightbulb className="h-3.5 w-3.5" />
                                            <span className="hidden xl:inline">Solution</span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>View Solution</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}

                        {/* Expanded Tools - Visible on MD+ screens (Direct Access) */}
                        <div className="hidden md:flex items-center gap-2 animate-in fade-in slide-in-from-right-4">
                            <TooltipProvider>
                                {/* Dictionary and Vocab Box - Only for Reading & Writing */}
                                {!isMath && (
                                    <>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant={activeTool === 'dictionary' ? "default" : "outline"}
                                                    size="sm"
                                                    aria-label="Toggle Dictionary"
                                                    onClick={() => onToolChange && onToolChange(activeTool === 'dictionary' ? null : 'dictionary')}
                                                    className={cn(
                                                        "h-8 text-base gap-2 rounded-full border-primary/20",
                                                        btn3d,
                                                        activeTool === 'dictionary'
                                                            ? "bg-primary/10 text-primary hover:bg-primary/20 border-primary/30"
                                                            : "bg-card text-muted-foreground hover:text-foreground"
                                                    )}
                                                >
                                                    <Book className="h-3.5 w-3.5" />
                                                    <span className="hidden xl:inline">Dictionary</span>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Dictionary</p>
                                            </TooltipContent>
                                        </Tooltip>

                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant={activeTool === 'vocab' ? "default" : "outline"}
                                                    size="sm"
                                                    aria-label="Toggle Vocab Box"
                                                    onClick={() => onToolChange && onToolChange(activeTool === 'vocab' ? null : 'vocab')}
                                                    className={cn(
                                                        "h-8 text-base gap-2 rounded-full border-primary/20",
                                                        btn3d,
                                                        activeTool === 'vocab'
                                                            ? "bg-primary/10 text-primary hover:bg-primary/20 border-primary/30"
                                                            : "bg-card text-muted-foreground hover:text-foreground"
                                                    )}
                                                >
                                                    <BookA className="h-3.5 w-3.5" />
                                                    <span className="hidden xl:inline">Vocab Box</span>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Vocab Box</p>
                                            </TooltipContent>
                                        </Tooltip>

                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant={isHighlightActive ? "default" : "outline"}
                                                    size="sm"
                                                    aria-label="Toggle Highlighter"
                                                    onClick={onToggleHighlight}
                                                    className={cn(
                                                        "h-8 text-base gap-2 rounded-full border-primary/20",
                                                        btn3d,
                                                        isHighlightActive
                                                            ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-300 dark:bg-yellow-500/20 dark:text-yellow-300 dark:border-yellow-500/30"
                                                            : "bg-card text-muted-foreground hover:text-foreground"
                                                    )}
                                                >
                                                    <Highlighter className="h-3.5 w-3.5" />
                                                    <span className="hidden xl:inline">Highlight</span>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Highlighter</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </>
                                )}

                            </TooltipProvider>
                        </div>





                        {
                            (title.toLowerCase().includes("math") ||
                                title.toLowerCase().includes("algebra") ||
                                title.toLowerCase().includes("geometry") ||
                                title.toLowerCase().includes("calculus") || // Just in case
                                title.toLowerCase().includes("trigonometry") ||
                                title.toLowerCase().includes("problem solving") || // Data Analysis
                                title.toLowerCase().includes("data analysis")) && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                aria-label="Reference Sheet"
                                                className={cn("h-8 text-base gap-2 rounded-full hidden md:flex bg-card border-primary/20 text-muted-foreground hover:text-foreground", btn3d)}
                                                onClick={onToggleReference}
                                            >
                                                <FileText className="h-3.5 w-3.5" />
                                                <span className="hidden xl:inline">Reference</span>
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Reference Sheet</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )
                        }
                        {/* Independent Dictionary button removed as it's now in the toolbox, OR should we keep it for non-assist mode? 
                        User request implied merging into toolbox "dropdown... organized". 
                        The original code had specific buttons. Assume we replace the independent Dictionary button if it's redundant.
                        The original design had Calculator, Reference, Dictionary. 
                        If the Toolbox has Dictionary, maybe we hide the standalone one to reduce clutter/redundancy?
                        Let's keep Calculator & Reference, but Dictionary is now inside.
                    */}
                    </div >
                </div >
            </header >
        </>
    )
})

SessionHeader.displayName = "SessionHeader"
